/* <td-size-finder> (sections/td-size-finder.liquid): garment tabs, in/cm, size chips driving the tee diagram and
   readout, and "match a tee you own" (closest size by width, length as tie-breaker). Without JS the table shows. */
if (!customElements.get('td-size-finder')) {
  customElements.define(
    'td-size-finder',
    class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        const opts = { signal: this.abort.signal };
        this.classList.add('is-ready');
        this.unit = 'in';

        this.querySelectorAll('[data-unit-btn]').forEach((b) =>
          b.addEventListener('click', () => this.setUnit(b.dataset.unitBtn), opts)
        );
        this.querySelectorAll('[data-garment-tab]').forEach((t) =>
          t.addEventListener('click', () => this.setGarment(t.dataset.garmentTab), opts)
        );
        this.querySelectorAll('.td-sf__panel').forEach((panel) => {
          const chips = [...panel.querySelectorAll('.td-sf__chip')];
          chips.forEach((chip, i) => {
            chip.addEventListener('click', () => this.pick(panel, chip), opts);
            chip.addEventListener(
              'keydown',
              (e) => {
                const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
                if (!step) return;
                e.preventDefault();
                const next = chips[(i + step + chips.length) % chips.length];
                this.pick(panel, next);
                next.focus();
              },
              opts
            );
          });
          panel.querySelector('[data-match]')?.addEventListener(
            'submit',
            (e) => {
              e.preventDefault();
              this.match(panel, e.target);
            },
            opts
          );
          const start = panel.querySelector('.td-sf__chip[aria-checked="true"]') || chips[0];
          if (start) this.pick(panel, start);
        });
      }

      fmt(inches) {
        const v = this.unit === 'cm' ? inches * 2.54 : inches;
        return this.unit === 'cm' ? String(Math.round(v)) : String(Math.round(v * 10) / 10);
      }

      setUnit(unit) {
        this.unit = unit;
        this.dataset.unit = unit;
        this.querySelectorAll('[data-unit-btn]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unitBtn === unit)));
        this.querySelectorAll('[data-out-unit]').forEach((u) => (u.textContent = unit));
        this.querySelectorAll('.td-sf__panel').forEach((panel) => {
          const chip = panel.querySelector('.td-sf__chip[aria-checked="true"]');
          if (chip) this.pick(panel, chip);
          panel.querySelectorAll('[data-match] input').forEach((inp) => (inp.value = ''));
          const res = panel.querySelector('[data-match-result]');
          if (res) res.textContent = '';
        });
      }

      setGarment(handle) {
        this.querySelectorAll('[data-garment-tab]').forEach((t) => t.setAttribute('aria-selected', String(t.dataset.garmentTab === handle)));
        this.querySelectorAll('.td-sf__panel').forEach((p) => (p.hidden = p.dataset.garment !== handle));
      }

      pick(panel, chip) {
        const chips = [...panel.querySelectorAll('.td-sf__chip')];
        chips.forEach((c) => {
          c.setAttribute('aria-checked', String(c === chip));
          c.tabIndex = c === chip ? 0 : -1;
        });
        const w = parseFloat(chip.dataset.w);
        const l = parseFloat(chip.dataset.l);
        panel.querySelector('[data-out-size]').textContent = chip.dataset.size;
        panel.querySelector('[data-out-w]').textContent = this.fmt(w);
        panel.querySelector('[data-out-l]').textContent = this.fmt(l);
        const chest = panel.querySelector('[data-out-chest]');
        if (chest) chest.textContent = `All the way around the tee: about ${this.fmt(w * 2)} ${this.unit}`;
        panel.querySelectorAll('tr[data-row]').forEach((r) => r.classList.toggle('is-picked', r.dataset.row === chip.dataset.size));

        // Diagram: scale the tee between the smallest and largest size so the step is visible.
        const ws = chips.map((c) => parseFloat(c.dataset.w));
        const ls = chips.map((c) => parseFloat(c.dataset.l));
        const sx = 0.86 + 0.24 * ((w - Math.min(...ws)) / Math.max(1, Math.max(...ws) - Math.min(...ws)));
        const sy = 0.9 + 0.16 * ((l - Math.min(...ls)) / Math.max(1, Math.max(...ls) - Math.min(...ls)));
        panel.querySelector('[data-tee]')?.style.setProperty('--td-sf-sx', sx.toFixed(3));
        panel.querySelector('[data-tee]')?.style.setProperty('--td-sf-sy', sy.toFixed(3));
      }

      match(panel, form) {
        const res = panel.querySelector('[data-match-result]');
        const toIn = (v) => (this.unit === 'cm' ? v / 2.54 : v);
        const w = parseFloat(form.w.value);
        const l = parseFloat(form.l.value);
        if (!(w > 0)) {
          res.textContent = 'Enter the width of your tee first.';
          form.w.focus();
          return;
        }
        const wIn = toIn(w);
        const lIn = l > 0 ? toIn(l) : null;
        let best = null;
        panel.querySelectorAll('.td-sf__chip').forEach((c) => {
          const cw = parseFloat(c.dataset.w);
          const cl = parseFloat(c.dataset.l);
          const score = Math.abs(cw - wIn) * 2 + (lIn ? Math.abs(cl - lIn) : 0);
          if (!best || score < best.score) best = { c, score, cw, cl };
        });
        if (!best) return;
        this.pick(panel, best.c);
        const diff = best.cw - wIn;
        const near = Math.abs(diff) < 0.5;
        const dir = diff > 0 ? 'wider' : 'narrower';
        res.textContent = near
          ? `Go for ${best.c.dataset.size}: about the same width as your tee.`
          : `Closest is ${best.c.dataset.size}: about ${this.fmt(Math.abs(diff))} ${this.unit} ${dir} than your tee.`;
      }

      disconnectedCallback() {
        this.abort?.abort();
      }
    }
  );
}
